import { Injectable } from '@angular/core';
import { interval, Subject, Subscription } from 'rxjs';
import { filter, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  FeatureNames,
  GatewayRemoteDiagnosticsStatus,
  RemoteDiagnosticsCommands,
  RemoteDiagnosticsProperties,
} from '@vi-iot/client';

import {
  DIAGNOSTICS_CONNECTION_STEP,
  InstallationFeaturesStore,
  isReady,
  localStorageSessionsKey,
  RemoteDiagnosticsStore,
} from '@store';
import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
@Injectable({
  providedIn: 'root',
})
export class RemoteDiagnosticsBackgroundService {
  logicalLinkListInterval: Subscription;
  private intervalTime = 10 * 1000;
  private stopInterval$: Subject<void>;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private appInsights: AppInsightsMonitoringService
  ) {}

  initLogicalLinkListInterval(gatewaySerial: string): void {
    this.appInsights.logEvent(
      APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.START_POLLING_LOGICAL_LINK_LIST,
      LogLevelEnum.TRACE
    );

    this.stopInterval$ = new Subject<void>();
    if (this.logicalLinkListInterval) {
      this.logicalLinkListInterval.unsubscribe();
      this.logicalLinkListInterval = null;
    }
    this.logicalLinkListInterval = interval(this.intervalTime)
      .pipe(
        startWith(0),
        withLatestFrom(this.remoteDiagnosticsStore.getActiveConnectionStep$),
        filter(
          ([_number, modalStep]) =>
            modalStep !== DIAGNOSTICS_CONNECTION_STEP.CONNECTION_ESTABLISHING
        ),
        takeUntil(this.stopInterval$)
      )
      .subscribe(() => {
        this.remoteDiagnosticsStore.loadLogicalLinkList(gatewaySerial);
      });
  }

  stopIntervals(): void {
    if (this.stopInterval$) {
      this.stopInterval$.next();
      this.stopInterval$.complete();
      this.stopInterval$ = null;
    }
    this.logicalLinkListInterval = null;
  }

  subscribeToRemoteDiagnosticsStatusFeature(gatewaySerial: string): void {
    this.installationFeaturesStore
      .selectInstallationDeviceFeature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>(
        FeatureNames.gatewayRemoteDiagnostics
      )
      .pipe(
        filter((feature) => isReady(feature)),
        filter(
          (feature) => feature.properties.status?.value === GatewayRemoteDiagnosticsStatus.Inactive
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        const localStorageKey = `${localStorageSessionsKey}${gatewaySerial}`;
        if (localStorage.getItem(localStorageKey)) {
          localStorage.removeItem(localStorageKey);
        }
      });
  }

  resetSubscriptions(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.ngUnsubscribe = new Subject<void>();
  }
}
